/**
 * @module components/CustomMetricSort
 * @description 指标排序编辑器 — UI 对齐 ChartsDrop/CustomMetricSort
 *
 * 受控模式（推荐）：传入 value / onChange / customOptions
 * Context 模式：不传 value/onChange 时从 ChartSettingsContext 读写 chartSorts
 */

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useIntl } from 'umi';
import { Radio, Select } from 'tea-component';
import SvgIcon from '@home/components/SvgIcon';
import { LANGUAGE_EN } from '@home/_config/config';
import { CSActionType } from '../../../../types';
import {
  readStructuredArrayRespectingDraftKey,
  useChartSettingsSelector,
  useChartSettingsDispatch,
} from '../../../../context';
import type { IMetricSortValueItem } from '../../../../types/settings/sortFields';
import './index.scss';

export interface ICustomMetricSortOption {
  value: string;
  table_code?: string;
  column_name?: string;
  column_display?: string;
}

export type ICustomMetricSortValueItem = IMetricSortValueItem;

interface ICustomMetricSortProps {
  value?: ICustomMetricSortValueItem[];
  onChange?: (value: ICustomMetricSortValueItem[]) => void;
  customOptions?: ICustomMetricSortOption[];
  /** 受控模式下是否允许自动回填默认值 */
  enabled?: boolean;
  visibleItems?: Record<string, boolean>;
}

const CustomMetricSortInner: React.FC<
  ICustomMetricSortProps & {
    value: ICustomMetricSortValueItem[];
    onChange: (value: ICustomMetricSortValueItem[]) => void;
  }
> = ({ value, onChange, customOptions, enabled = true }) => {
  const { formatMessage, locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;
  const [metricKey, setMetricKey] = useState(value?.[0]?.key || '');
  const [sort, setSort] = useState<ICustomMetricSortValueItem['value']>(
    value?.[0]?.value || 'asc',
  );

  const defaultItem = useMemo<ICustomMetricSortValueItem | undefined>(() => {
    if (!customOptions?.length) return undefined;
    return {
      key: customOptions[0]?.value || '',
      value: 'asc',
      table_code: customOptions[0]?.table_code || 't0',
    };
  }, [customOptions]);

  useEffect(() => {
    if (!value?.length) {
      if (!defaultItem) return;
      setMetricKey(defaultItem.key);
      setSort(defaultItem.value);
      if (enabled) {
        onChange([defaultItem]);
      }
      return;
    }
    setMetricKey(value[0]?.key || '');
    setSort(value[0]?.value || 'asc');
  }, [defaultItem, enabled, onChange, value]);

  const options = useMemo(
    () =>
      customOptions?.map((item) => ({
        ...item,
        text: item?.column_display || item?.column_name || item.value,
      })) || [],
    [customOptions],
  );

  /** QB 仅改展示名时 value 不变，强制 Select 随 options.text 刷新按钮文案 */
  const metricSelectKey = useMemo(
    () =>
      options.map((item) => `${item.value}:${item.text}`).join('|') || 'empty',
    [options],
  );

  return (
    <div className="dbl-chart-custom-metric-sort">
      <div className="dbl-chart-custom-metric-sort-title">
        <div className="dbl-chart-custom-metric-sort-label">
          {formatMessage({ id: 'dbl_chart_metric' })}
        </div>
        <Select
          key={`${metricKey}::${metricSelectKey}`}
          className="dbl-chart-custom-metric-sort-select"
          boxClassName="dbl-chart-custom-metric-sort-select-box"
          value={metricKey}
          options={options}
          matchButtonWidth
          appearance="button"
          onChange={(nextMetricKey) => {
            if (nextMetricKey === metricKey) return;
            setMetricKey(nextMetricKey);
            setSort('asc');
            const tableCode = customOptions?.find(
              (item) => item.value === nextMetricKey,
            )?.table_code;
            onChange([
              { key: nextMetricKey, value: 'asc', table_code: tableCode || 't0' },
            ]);
          }}
        />
      </div>
      <div
        className={`dbl-chart-custom-metric-sort-content dbl-chart-custom-metric-sort-content-${isEn ? 'en' : 'zh'}`}
      >
        <Radio.Group
          value={sort}
          onChange={(nextSort) => {
            const normalizedSort: ICustomMetricSortValueItem['value'] =
              nextSort === 'desc' ? 'desc' : 'asc';
            setSort(normalizedSort);
            const tableCode = customOptions?.find(
              (item) => item.value === metricKey,
            )?.table_code;
            onChange([
              { key: metricKey, value: normalizedSort, table_code: tableCode || 't0' },
            ]);
          }}
        >
          <Radio name="asc">
            {formatMessage({ id: 'dbl_chart_low' })}
            <SvgIcon type="arrow-big-up" className="dbl-sort-rate-icon-arrow" />
          </Radio>
          <Radio name="desc">
            {formatMessage({ id: 'dbl_chart_high' })}
            <SvgIcon type="arrow-big-down" className="dbl-sort-rate-icon-arrow" />
          </Radio>
        </Radio.Group>
      </div>
    </div>
  );
};

const CustomMetricSort: React.FC<ICustomMetricSortProps> = (props) => {
  const {
    value: valueProp,
    onChange: onChangeProp,
    customOptions: customOptionsProp,
    enabled,
  } = props;
  const dispatch = useChartSettingsDispatch();

  const contextValue = useChartSettingsSelector(s =>
    readStructuredArrayRespectingDraftKey(s.draft, s.base, 'chartSorts'),
  );

  const handleContextChange = useCallback(
    (newValue: ICustomMetricSortValueItem[]) => {
      dispatch({
        type: CSActionType.UPDATE_DRAFT,
        payload: { chartSorts: newValue, customMetricSort: newValue },
      });
    },
    [dispatch],
  );

  const isControlled = onChangeProp !== undefined;

  if (!isControlled && (!contextValue || contextValue.length === 0) && !customOptionsProp?.length) {
    return null;
  }

  return (
    <CustomMetricSortInner
      value={isControlled ? (valueProp ?? []) : contextValue}
      onChange={isControlled ? onChangeProp! : handleContextChange}
      customOptions={customOptionsProp}
      enabled={enabled ?? true}
    />
  );
};

export default React.memo(CustomMetricSort);
